import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircle, Tag, Check, ArrowRight } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Computers & Language",
        icon: "🦜",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Parrot Talk</h3>
                    <p className="text-slate-700 mb-4">
                        The jungle animals speak English, but Computers speak <strong>JSON</strong> (JavaScript Object Notation).
                    </p>
                    <p className="text-slate-700">
                        It's a way to label things using <strong>Key</strong> and <strong>Value</strong> pairs,
                        just like a parrot repeating exactly what you say in a specific format!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Keys and Values",
        icon: "🔑",
        content: (
            <div className="space-y-6">
                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200">
                    <h3 className="font-bold text-xl text-orange-800 mb-3">The Pair Rule</h3>
                    <p className="text-slate-700 mb-4">
                        Every piece of data needs a label (Key) and the data itself (Value).
                    </p>
                    <div className="font-mono text-sm bg-slate-800 text-white p-4 rounded-lg">
                        <div><span className="text-purple-400">"name"</span>: <span className="text-green-400">"Gorilla"</span></div>
                        <div><span className="text-purple-400">"job"</span>: <span className="text-green-400">"Explorer"</span></div>
                        <div><span className="text-purple-400">"level"</span>: <span className="text-blue-400">5</span></div>
                    </div>
                </div>
                <div className="bg-slate-100 p-4 rounded-xl border border-slate-200">
                    <ul className="text-slate-700 text-sm space-y-2">
                        <li>• <strong>Keys</strong> are always in "quotes"</li>
                        <li>• <strong>Strings</strong> are in "quotes"</li>
                        <li>• <strong>Numbers</strong> are NOT in quotes</li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Objects",
        icon: "📦",
        content: (
            <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200">
                    <h3 className="font-bold text-xl text-purple-800 mb-3">The Curly Braces</h3>
                    <p className="text-slate-700 mb-4">
                        We wrap related key-value pairs in curly braces <code>{`{ }`}</code> to make an <strong>Object</strong>.
                    </p>
                    <div className="font-mono text-sm bg-slate-800 text-white p-4 rounded-lg">
                        <div><span className="text-yellow-500">{'{'}</span></div>
                        <div className="pl-4"><span className="text-purple-400">"name"</span>: <span className="text-green-400">"Parrot"</span>,</div>
                        <div className="pl-4"><span className="text-purple-400">"color"</span>: <span className="text-green-400">"Green"</span></div>
                        <div><span className="text-yellow-500">{'}'}</span></div>
                    </div>
                </div>
                <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                    <h4 className="font-bold text-green-800 mb-2">🎮 Ready to Speak JSON?</h4>
                    <p className="text-green-700 text-sm">
                        In the activity, you'll build an Object to tell the parrot what to say!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
const ParrotGame = ({ onComplete }: { onComplete: () => void }) => {
    // Basic Object State
    const [name, setName] = useState('');
    const [job, setJob] = useState('Explorer');
    const [level, setLevel] = useState(1);

    // Validated State
    const [isValid, setIsValid] = useState(false);

    // Simulated "Speaking" State
    const [speaking, setSpeaking] = useState(false);

    const handleSquawk = () => {
        if (!name) return;
        setSpeaking(true);
        setTimeout(() => {
            setSpeaking(false);
            setIsValid(true);
            onComplete();
        }, 2000);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* LEFT: Builder Form */}
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-slate-100 relative">
                <h3 className="font-bold text-xl text-slate-700 mb-6 flex items-center gap-2">
                    <Tag className="text-red-500" /> Build the Object
                </h3>

                <div className="space-y-6">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Key: "name"</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => { setName(e.target.value); setIsValid(false); }}
                            placeholder="e.g. Gorilla"
                            className="w-full text-xl font-bold p-3 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Key: "job"</label>
                        <div className="flex gap-2">
                            {['Explorer', 'Chef', 'Runner', 'Wizard'].map(j => (
                                <button
                                    key={j}
                                    onClick={() => { setJob(j); setIsValid(false); }}
                                    className={`px-3 py-2 rounded-lg font-bold border-2 transition-colors ${job === j ? 'border-red-500 bg-red-50 text-red-600' : 'border-slate-100 text-slate-400'}`}
                                >
                                    {j}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Key: "level" (Number)</label>
                        <input
                            type="range"
                            min="1" max="100"
                            value={level}
                            onChange={e => { setLevel(Number(e.target.value)); setIsValid(false); }}
                            className="w-full accent-red-500"
                        />
                        <div className="text-right font-mono font-bold text-red-500">{level}</div>
                    </div>

                    <button
                        onClick={handleSquawk}
                        disabled={!name || speaking}
                        className="w-full bg-red-500 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-red-600 disabled:opacity-50 text-xl transition-transform active:scale-95 flex items-center justify-center gap-2"
                    >
                        {speaking ? 'Squawking...' : 'Squawk (Translate!)'} <MessageCircle size={24} />
                    </button>
                </div>
            </div>

            {/* RIGHT: The Parrot & Response */}
            <div className="relative flex flex-col items-center">

                {/* Speech Bubble */}
                <AnimatePresence>
                    {(speaking || isValid) && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="bg-slate-900 text-green-400 p-6 rounded-3xl shadow-2xl border-4 border-slate-700 font-mono text-sm sm:text-base relative mb-8 max-w-md w-full"
                        >
                            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-slate-900 border-r-4 border-b-4 border-slate-700 transform rotate-45"></div>

                            <div><span className="text-yellow-500">{'{'}</span></div>
                            <div className="pl-6">
                                "name": <span className="text-orange-300">"{name}"</span>,
                            </div>
                            <div className="pl-6">
                                "job": <span className="text-orange-300">"{job}"</span>,
                            </div>
                            <div className="pl-6">
                                "level": <span className="text-blue-300">{level}</span>
                            </div>
                            <div><span className="text-yellow-500">{'}'}</span></div>
                        </motion.div>
                    )}
                </AnimatePresence>


                <div className="relative w-80">
                    <img src="/assets/gorillas/smart_parrot.png" className="w-full drop-shadow-2xl" alt="Smart Parrot" />

                    {speaking && (
                        <motion.div
                            className="absolute top-0 right-0 text-4xl"
                            animate={{ opacity: [0, 1, 0], y: -20, x: 20 }}
                            transition={{ repeat: Infinity, duration: 0.5 }}
                        >
                            🎵
                        </motion.div>
                    )}
                </div>

                {isValid && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-8 bg-green-100 text-green-800 p-4 rounded-xl flex items-center gap-4 shadow-sm"
                    >
                        <div className="bg-white p-2 rounded-full shadow-sm text-green-600">
                            <Check size={24} />
                        </div>
                        <div>
                            <p className="font-bold">JSON Created!</p>
                            <p className="text-sm">You've successfully built an object.</p>
                        </div>
                    </motion.div>
                )}

            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const ParrotTalk = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('objects-json', 50, 'badge_foundation');
    };

    return (
        <LessonWrapper
            moduleName="Parrot Talk"
            moduleEmoji="🦜"
            moduleColor="blue"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Activity"
        >
            <ParrotGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default ParrotTalk;
